import { gql } from '@apollo/client';
import { Outlet, useParams, useSearchParams } from 'react-router-dom';

import { LoadingImage } from 'component-lib/atoms/LoadingImage/LoadingImage';
import { Mode, usePlayAppContainerQuery } from 'types/graphql';

import { LabMetadataContext } from './context/LabMetadataContext';
import { getFullscreenIsSupported } from './support/fullscreen';
import { isSkippingEnabled } from './support/isSkippingEnabled';
import DarkOverlay from './templates/dark-overlay/DarkOverlay';

interface PlayAppContainerProps {
  mode: Mode;
}

export const PlayAppContainer = ({ mode }: PlayAppContainerProps) => {
  const { teamSlug = '', trackSlug = '', inviteID } = useParams<'teamSlug' | 'trackSlug' | 'inviteID'>();
  const [searchParams] = useSearchParams();

  const userID = searchParams.get('userID');

  const { data, loading } = usePlayAppContainerQuery({
    variables: { teamSlug, trackSlug, userID },
  });

  const timelimit = data?.track
    ? Math.ceil(
        Number(data?.track?.timelimit) || data?.track?.challenges.reduce((acc, curr) => acc + (curr.timelimit ?? 0), 0),
      )
    : 0;

  if (loading) {
    return (
      <DarkOverlay>
        <LoadingImage />
      </DarkOverlay>
    );
  }

  const skippingEnabled = isSkippingEnabled(data);
  const fullScreenSupported = getFullscreenIsSupported();
  const viewportSize = `${window.innerWidth}x${window.innerHeight}`;
  const screenSize = `${window.screen.width}x${window.screen.height}`;

  const isInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  return (
    <LabMetadataContext
      trackId={data?.track?.id}
      trackSkippable={skippingEnabled}
      inviteId={inviteID}
      teamSlug={teamSlug}
      mode={mode}
      isInIframe={isInIframe()}
      fullScreenSupported={fullScreenSupported}
      numberOfChallenges={data?.track?.challenges.length}
      medianStartTime={data?.track?.median_starttime || 0}
      timeLimit={timelimit}
      idleTimeout={data?.track?.idle_timeout || 0}
      participantId={data?.track?.participant?.id}
      isHotStarted={data?.track?.participant?.hotStartPool?.id ? true : false}
      inInspectMode={!!userID}
      viewportSize={viewportSize}
      screenSize={screenSize}
    >
      <Outlet />
    </LabMetadataContext>
  );
};

gql`
  query PlayAppContainer($trackSlug: String!, $teamSlug: String!, $userID: String) {
    track(trackSlug: $trackSlug, teamSlug: $teamSlug, userID: $userID) {
      id
      challenges {
        id
        timelimit
      }
      median_starttime
      idle_timeout
      timelimit
      participant {
        id
        hotStartPool {
          id
        }
      }
    }

    ...IsSkippingEnabledQuery
  }
`;
